import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import emissionPerEmployeeIcon from '../../../../images/emission_per_employee.png';
import emissionTotalIcon from '../../../../images/emission_total.png';
import scope1Icon from '../../../../images/scope1.png';
import scope2Icon from '../../../../images/scope2.png';
import scope3Icon from '../../../../images/scope3.png';
import styles from './styles.module.css';
import InfoModal from '../../InfoModal/infoModal';
import Percent from './Percent';
import {
  DashboardStats,
  DashboardStatsChanges,
} from '../../../../redux-file/charts/interface';
import { Block, Container, Main, Units, Wrapper } from './index.styles';
import SomethingWentWrong from '../../../../components/SomethingWentWrong';
import { IStatsProps } from './index.interface';
import QuestionnaireTitle from '../../Questionnaire/Title';
import { IntuitiveCalendarCollapsable } from '../../IntuitiveCalendar';
import { ReportingFreq } from '../../../../redux-file/interfaces';
import { StyledFlex } from 'src/components/StyledFlex';
import { useLanguage } from 'src/common/hooks';
import { GhgSupplierSurveySummary } from 'src/Ghg/interfaces';

export type TimeRange = {
  start: Date;
  end: Date;
};
// Keeps track of last selected time range
export const useValidTimeRange = (
  startTime: Date | null,
  endTime: Date | null
) => {
  const [timeRange, setTimeRange] = useState<TimeRange | null>(null);
  useEffect(() => {
    if (startTime && endTime) {
      setTimeRange({ start: startTime, end: endTime });
    }
  }, [startTime, endTime]);
  return timeRange;
};

export default function Stats({
  data,
  surveysSummary,
  dataChanges,
  calendar,
  startTime,
  endTime,
  setStartTime,
  setEndTime,
  reportingPeriod,
  hideScope3,
}: {
  data: DashboardStats;
  surveysSummary: GhgSupplierSurveySummary[] | undefined;
  dataChanges: DashboardStatsChanges;
  calendar: boolean;
  startTime: Date | null;
  setStartTime: Dispatch<SetStateAction<Date | null>>;
  endTime: Date | null;
  setEndTime: Dispatch<SetStateAction<Date | null>>;
  reportingPeriod: ReportingFreq;
  hideScope3?: boolean;
}) {
  if (!data) return <SomethingWentWrong />;

  const dataCopy = { ...data } as DashboardStats & {
    [key: string]: number | null;
  };
  const {
    emissionPerEmployee,
    emissionTotal,
    emissionScope1,
    emissionScope3,
    emissionScope2LocationBasedMethod,
    emissionScope2MarketBasedMethod,
    emissionPerIncome,
    emissionPerIncomeCurrency,
    energyConsumption,
  } = Object.keys(dataCopy).reduce((acc, key) => {
    const transformedKey = key.replace(/(_\w)/g, (m) => m[1].toUpperCase());
    return { ...acc, [transformedKey]: dataCopy[key] };
  }, {} as IStatsProps);

  const { t, language } = useLanguage();

  const formatNumber = (num: number) =>
    parseFloat(num.toFixed(3)).toLocaleString(language);

  // Raben 14.10: every Scope 1 and Scope 2 section marked as completed AND
  // at least one Scope 1 entry -- boxes ticked over empty forms are not a
  // calculation. The backend's is_calculator_complete (ghg_suppliers/api/
  // utils.py) is the same rule for the per-country card.
  const isCalculatorCompleted = (survey: GhgSupplierSurveySummary) =>
    survey.progress_percentage !== null &&
    survey.progress_percentage >= 100 &&
    survey.scope_1_entries > 0;

  // RG 28.08 3.2: the three supplier tiles each count ONE thing (rows of the
  // summary, i.e. supplier x period -- not de-duplicated by VAT, same as the
  // per-country table):
  //  - "calculated their emissions" = scopes 1+2 complete in the calculator
  //  - "completed the ESG Survey"   = ESG questionnaire submitted
  //  - "have their own CO2 report"  = environment questionnaire submitted
  //    (the SUPPLIER-SHORT path); the calculator no longer counts here.
  const suppliersEmissionsCalculatedCount = surveysSummary
    ? surveysSummary.filter(isCalculatorCompleted).length
    : '-';

  const suppliersEsgSurveyCompletedCount = surveysSummary
    ? surveysSummary.filter(
        (survey) => survey.esg_questionnaire_submitted_at !== null
      ).length
    : '-';

  const suppliersOwnCo2ReportCount = surveysSummary
    ? surveysSummary.filter(
        (survey) => survey.environment_questionnaire_submitted_at !== null
      ).length
    : '-';

  return (
    <Container>
      <StyledFlex sx={{ p: '10px 20px' }}>
        <QuestionnaireTitle text={t('stats.title')} />

        {calendar && (
          <IntuitiveCalendarCollapsable
            reportingPeriod={reportingPeriod}
            startDate={startTime}
            setStartDate={setStartTime}
            endDate={endTime}
            setEndDate={setEndTime}
          />
        )}
      </StyledFlex>

      <Wrapper>
        <Block>
          <p className={styles.title}>
            <img src={emissionTotalIcon} alt="" />
            {t('stats.total-emission')}
          </p>
          <Main>
            {Number.isFinite(emissionTotal)
              ? formatNumber(emissionTotal!)
              : '--'}
            <Units>
              {'  '}t CO<sub>2</sub>eq
            </Units>
          </Main>
          <Percent percent={dataChanges.emission_total_change} />
        </Block>

        <Block>
          <div className={styles.titleWrapper}>
            <p className={styles.title}>
              <img src={emissionPerEmployeeIcon} alt="" />
              {t('stats.suppliers-emissions-calculated')}
            </p>
          </div>
          <Main>{suppliersEmissionsCalculatedCount}</Main>
        </Block>

        <Block>
          <div className={styles.titleWrapper}>
            <p className={styles.title}>
              <img src={emissionPerEmployeeIcon} alt="" />
              {t('stats.suppliers-esg-survey-completed')}
            </p>
          </div>
          <Main>{suppliersEsgSurveyCompletedCount}</Main>
        </Block>

        <Block>
          <div className={styles.titleWrapper}>
            <p className={styles.title}>
              <img src={emissionPerEmployeeIcon} alt="" />
              {t('stats.suppliers-own-co2-report')}
            </p>
          </div>
          <Main>{suppliersOwnCo2ReportCount}</Main>
        </Block>

        <Block>
          <div className={styles.titleWrapper}>
            <p className={styles.title}>
              <img src={scope1Icon} alt="" />
              {t('stats.scope1')}
            </p>
            <InfoModal>
              <p>{t('stats.scope1-info')}</p>
            </InfoModal>
          </div>
          <Main>
            {Number.isFinite(emissionScope1)
              ? formatNumber(emissionScope1!)
              : '--'}
            <Units>
              {'  '}t CO<sub>2</sub>eq
            </Units>
          </Main>
          <Percent percent={dataChanges.emission_scope_1_change} />
        </Block>

        <Block>
          <div className={styles.titleWrapper}>
            <p className={styles.title}>{t('stats.market-method')}</p>
            <InfoModal>
              <p
                dangerouslySetInnerHTML={{
                  __html: t('stats.market-method-info'),
                }}
              />
            </InfoModal>
          </div>

          <Main>
            {Number.isFinite(emissionScope2MarketBasedMethod)
              ? formatNumber(emissionScope2MarketBasedMethod!)
              : '--'}
            <Units>
              {'  '}t CO<sub>2</sub>eq
            </Units>
          </Main>
          <Percent
            percent={dataChanges.emission_scope_2_market_based_method_change}
          />
        </Block>
        <Block>
          <div className={styles.titleWrapper}>
            <p className={styles.title}>{t('stats.local-method')}</p>
            <InfoModal>
              <p
                dangerouslySetInnerHTML={{
                  __html: t('stats.local-method-info'),
                }}
              />
            </InfoModal>
          </div>

          <Main>
            {Number.isFinite(emissionScope2LocationBasedMethod)
              ? formatNumber(emissionScope2LocationBasedMethod!)
              : '--'}
            <Units>
              {'  '}t CO<sub>2</sub>eq
            </Units>
          </Main>
          <Percent
            percent={dataChanges.emission_scope_2_location_based_method_change}
          />
        </Block>

        <Block>
          <p className={styles.title}>
            <img className={styles.titleImageBig} src={scope2Icon} alt="" />
            {t('stats.energy-consumption')}
          </p>

          <Main>
            {Number.isFinite(energyConsumption)
              ? formatNumber(energyConsumption!)
              : '--'}
            <Units>{'  '}MWh</Units>
          </Main>
          <Percent percent={dataChanges.energy_consumption_change} />
        </Block>
      </Wrapper>
    </Container>
  );
}
